import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useFeatureFlag } from 'contexts';

import ScrollToTop from 'app/components/ScrollToTop/ScrollToTop';
import selectAuthenticationState from 'app/domain/authentication/slice/selectors';
import SequentialPage from 'app/domain/loan/SequentialPage';
import { ApplicationApprovedPage } from 'app/pages/ApplicationApprovedPage';
import { ApplicationIdNotFoundErrorPage } from 'app/pages/ApplicationIdNotFoundErrorPage';
import { ApplicationSubmissionPage } from 'app/pages/ApplicationSubmissionPage';
import { ApplicationTrackingPage } from 'app/pages/ApplicationTrackingPage';
import { BranchSelectionPage } from 'app/pages/BranchSelectionPage';
import { ChangePhoneNumberPage } from 'app/pages/ChangePhoneNumberPage';
import { CollateralConfirmationPage } from 'app/pages/CollateralConfirmationPage';
import { DocumentInformationPage } from 'app/pages/DocumentInformationPage';
import { FaceVerificationPage } from 'app/pages/FaceVerificationPage';
import { KTPReviewInfoPage } from 'app/pages/KTPReviewInfoPage';
import { KTPVerificationPage } from 'app/pages/KTPVerificationPage';
import { KYCPage } from 'app/pages/KYCPage';
import { LandingPage } from 'app/pages/LandingPage';
import { LeadExpiredErrorPage } from 'app/pages/LeadExpiredErrorPage';
import { LeadRevivePage } from 'app/pages/LeadRevivePage';
import { LeadVerificationPage } from 'app/pages/LeadVerificationPage';
import { LoanEstimationPage } from 'app/pages/LoanEstimationPage';
import InstallmentList from 'app/pages/LoanEstimationPage/InstallmentList';
import { VehicleBrandsPage } from 'app/pages/LoanSimulation/VehicleBrandsPage';
import { VehicleDetailPage } from 'app/pages/LoanSimulation/VehicleDetailPage';
import { VehicleModelsPage } from 'app/pages/LoanSimulation/VehicleModelsPage';
import { VehicleVariantsPage } from 'app/pages/LoanSimulation/VehicleVariantsPage';
import { VehicleYearsPage } from 'app/pages/LoanSimulation/VehicleYearsPage';
import { NotFoundPage } from 'app/pages/NotFoundPage';
import { OfflineTouchPointPage } from 'app/pages/OfflineTouchPointPage';
import { OTPPage } from 'app/pages/OTPPage';
import { PersonalInformationPage } from 'app/pages/PersonalInformationPage';
import { ReviewApplicationPage } from 'app/pages/ReviewApplicationPage';
import { TakeSelfieOnlyPage } from 'app/pages/TakeSelfieOnlyPage';
import { VehicleAndLoanReview } from 'app/pages/VehicleAndLoanReview';

import { RoutePath } from './constants';

const ProtectedRoutes = () => {
  const { isAuthenticated } = useSelector(selectAuthenticationState);

  return isAuthenticated ? <Outlet /> : <Navigate to={RoutePath.landingPage} />;
};

function AppRouting() {
  const { isChangePhoneNumberEnabled, isRedirectEnabled } = useFeatureFlag();
  const { isAllowChangePhoneNumber } = useSelector(selectAuthenticationState);
  const sequentialRouting = () => {
    return (
      <>
        <Route path={RoutePath.verifyIdentityPage} element={<KYCPage />} />
        <Route
          path={RoutePath.faceVerificationPage}
          element={<FaceVerificationPage />}
        />
        <Route
          path={RoutePath.ktpVerificationPage}
          element={<KTPVerificationPage />}
        />
        <Route
          path={RoutePath.reviewInformation}
          element={<KTPReviewInfoPage />}
        />
        <Route path={RoutePath.vehicleBrand} element={<VehicleBrandsPage />} />
        <Route path={RoutePath.vehicleDetail} element={<VehicleDetailPage />} />
        <Route path={RoutePath.vehicleModel} element={<VehicleModelsPage />} />
        <Route path={RoutePath.vehicleYear} element={<VehicleYearsPage />} />
        <Route
          path={RoutePath.vehicleVariant}
          element={<VehicleVariantsPage />}
        />
        <Route
          path={RoutePath.personalInformation}
          element={<PersonalInformationPage />}
        />
        <Route
          path={RoutePath.reviewVehicleAndLoan}
          element={<VehicleAndLoanReview />}
        />
        <Route
          path={RoutePath.applicationSubmission}
          element={<ApplicationSubmissionPage />}
        />
        <Route
          path={RoutePath.reviewApplication}
          element={<ReviewApplicationPage />}
        />
        <Route
          path={RoutePath.offlineTouchPoint}
          element={<OfflineTouchPointPage />}
        />
        <Route
          path={RoutePath.loanEstimation}
          element={<LoanEstimationPage />}
        />
        <Route
          path={RoutePath.approvalInformation}
          element={<ApplicationApprovedPage />}
        />
        <Route
          path={RoutePath.loanEstimationInstallmentList}
          element={<InstallmentList />}
        />
        <Route
          path={RoutePath.takeSelfieOnly}
          element={<TakeSelfieOnlyPage />}
        />
        <Route path={RoutePath.leadRevivePage} element={<LeadRevivePage />} />
        <Route
          path={RoutePath.leadExpiredErrorPage}
          element={<LeadExpiredErrorPage />}
        />
      </>
    );
  };
  return (
    <ScrollToTop>
      <Routes>
        <Route path={RoutePath.landingPage} element={<LandingPage />} />
        <Route path={RoutePath.otpVerification} element={<OTPPage />} />
        <Route
          path={RoutePath.applicationIdErrorPage}
          element={<ApplicationIdNotFoundErrorPage />}
        />
        <Route
          path={RoutePath.collateralConfirmation}
          element={<CollateralConfirmationPage />}
        />
        <Route
          path={RoutePath.leadVerificationPage}
          element={<LeadVerificationPage />}
        />
        <Route
          path={RoutePath.documentInformation}
          element={<DocumentInformationPage />}
        />
        <Route
          path={RoutePath.branchSelection}
          element={<BranchSelectionPage />}
        />
        <Route
          path={RoutePath.applicationTracking}
          element={<ApplicationTrackingPage />}
        />
        <Route path="*" element={<NotFoundPage />} />
        <Route element={<ProtectedRoutes />}>
          {isRedirectEnabled ? (
            <Route element={<SequentialPage />}>{sequentialRouting()}</Route>
          ) : (
            <Route>{sequentialRouting()}</Route>
          )}
        </Route>
        {isChangePhoneNumberEnabled && isAllowChangePhoneNumber && (
          <Route
            path={RoutePath.changePhoneNumber}
            element={<ChangePhoneNumberPage />}
          />
        )}
      </Routes>
    </ScrollToTop>
  );
}

export default AppRouting;
